import {
  BRANDING_THEME_DEMO_BANK_ACCOUNT,
  BRANDING_THEME_SUBHEADER_DEMO_BOXES,
} from '@/constants/brandingThemes';
import { invertColor } from '@/utils/color';
import { formatCurrency } from '@/utils/currency';
import {
  AccountBalance,
  MonetizationOn,
  Send,
  SwapHorizontalCircle,
} from '@mui/icons-material';
import { Box, Typography, Grid, Card, Stack } from '@mui/material';

type WebsiteDemoProps = {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
};

export default function WebsiteDemo({
  primaryColor,
  secondaryColor,
  logoUrl,
}: WebsiteDemoProps) {
  const quickLinks = [
    {
      title: 'Deposit',
      icon: <MonetizationOn sx={{ color: primaryColor }} />,
    },
    {
      title: 'Transfer',
      icon: <SwapHorizontalCircle sx={{ color: primaryColor }} />,
    },
    {
      title: 'Send Wire',
      icon: <Send sx={{ color: primaryColor }} />,
    },
    {
      title: 'New Account',
      icon: <AccountBalance sx={{ color: primaryColor }} />,
    },
  ];

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Website Demo
      </Typography>
      <Box
        sx={{
          width: '100%',
          minHeight: 10,
          border: '1px solid black',
        }}>
        {/* Header */}
        <Box
          sx={{
            height: '70px',
            borderBottom: `3px solid ${primaryColor}`,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 2,
            paddingRight: 2,
          }}>
          <img
            style={{ maxWidth: '100%', maxHeight: '50px' }}
            alt="Mobile Logo"
            src={logoUrl}
          />
        </Box>
        {/* Subheader */}
        <Box
          sx={{
            backgroundColor: primaryColor,
            p: '24px 12px',
          }}>
          <Typography variant="h4" color={invertColor(primaryColor)} mb="16px">
            Welcome back
          </Typography>
          <Grid
            container
            gap={2}
            sx={{
              flexWrap: 'nowrap',
            }}>
            {BRANDING_THEME_SUBHEADER_DEMO_BOXES.map(box => (
              <Grid
                size={{ xs: 12, md: 4 }}
                key={box.title}
                sx={{
                  backgroundColor: secondaryColor,
                  p: '16px',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}>
                  <Typography variant="body1" color="white" mr={1}>
                    {box.title}
                  </Typography>
                </Box>
                <Typography color="white" fontWeight="bold" variant="h4">
                  {box.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box sx={{ padding: '30px 10px' }}>
          {/* Quick Links */}
          <Card elevation={3}>
            <Typography variant="h6" mb={2}>
              Quick Links
            </Typography>
            <Stack direction="row" spacing={3}>
              {quickLinks.map(button => (
                <Box
                  role="button"
                  sx={{ cursor: 'pointer' }}
                  key={button.title}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      width: '72px !important',
                      height: '72px',
                      alignItems: 'center',
                      padding: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      border: `1px solid #C7C7C7`,
                      borderRadius: 2,
                      '&:hover': {
                        background: '#f5f5f5',
                      },
                    }}>
                    {button.icon}
                  </Card>
                  <Typography
                    variant="body2"
                    mt={1}
                    textAlign="center"
                    color="black">
                    {button.title}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Card>
          {/* Bank Accounts List */}
          <Box
            sx={{
              backgroundColor: primaryColor,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              p: 2,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              mt: 2,
            }}>
            <Typography
              variant="h6"
              sx={{
                color: invertColor(primaryColor),
              }}
              tabIndex={0}>
              Bank Accounts (1)
            </Typography>
          </Box>
          <Card
            sx={{
              border: `1px solid #C7C7C7`,
              width: '100%',
              padding: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}>
            <Stack sx={{ m: 2 }} spacing="16px">
              {BRANDING_THEME_DEMO_BANK_ACCOUNT.map(account => {
                const {
                  id,
                  accountInfoLabel,
                  userInfoLabel,
                  availableBalance,
                  currentBalance,
                  pendingTransactionBalance,
                } = account;

                const styles = {
                  border: `1px solid #C7C7C7`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                    transition: 'opacity 0.2s',
                  },
                };

                return (
                  <Box key={id} sx={styles}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        borderTopRightRadius: 5,
                        borderTopLeftRadius: 5,
                      }}>
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-label={`Click to view ${accountInfoLabel} ${userInfoLabel} details`}
                        sx={{ flex: 1 }}>
                        <Typography>{accountInfoLabel}</Typography>
                        <Typography>{userInfoLabel}</Typography>
                      </Box>
                    </Box>
                    <Grid
                      alignItems="flex-end"
                      container
                      sx={{ padding: '16px 8px' }}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h4">
                          {formatCurrency({
                            amount: Number(availableBalance.value),
                          })}
                        </Typography>
                        <Typography color={primaryColor}>
                          Available Balance
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6">
                          {formatCurrency({
                            amount: Number(currentBalance.value),
                          })}
                        </Typography>
                        <Typography color={primaryColor}>
                          Current Balance
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6" color="#2e7d32">
                          {formatCurrency({
                            amount: Number(pendingTransactionBalance.value),
                          })}
                        </Typography>
                        <Typography color={primaryColor}>
                          Pending Deposits
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        </Box>
      </Box>
    </Card>
  );
}
